import { useMemo } from 'react'

import { useConvexAssessmentAverages } from '../services/assessments.convex'
import { ScoreCard } from './ScoreCard'
import { AssessmentRadarChart } from './AssessmentRadarChart'
import { CategoryScores } from './CategoryScores'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface AssessmentAnalyticsProps {
  assessmentId: string
  showOnlySummary?: boolean
  showOnlyScoreCards?: boolean
}

export function AssessmentAnalytics({
  assessmentId,
  showOnlySummary = false,
  showOnlyScoreCards = false,
}: AssessmentAnalyticsProps) {
  // Fetch assessment averages
  const averagesData = useConvexAssessmentAverages(assessmentId)
  const isLoading = averagesData === undefined

  // Calculate overall average scores for manager and direct report
  const overallScores = useMemo(() => {
    if (averagesData && averagesData.length > 0) {
      const managerData = averagesData.find(
        (avg) => avg.responseType === 'manager',
      )
      const directReportData = averagesData.find(
        (avg) => avg.responseType === 'self',
      )

      return {
        managerAvg: managerData?.overallAvg ?? 0,
        directReportAvg: directReportData?.overallAvg ?? 0,
      }
    }

    return { managerAvg: 0, directReportAvg: 0 }
  }, [averagesData])

  // Prepare radar chart data by category
  const radarData = useMemo(() => {
    if (averagesData && averagesData.length > 0) {
      const managerData = averagesData.find(
        (avg) => avg.responseType === 'manager',
      )
      const directReportData = averagesData.find(
        (avg) => avg.responseType === 'self',
      )

      // Map category names from API (craftAvg, talentAvg, etc.) to display names
      const categoryMap: Record<string, string> = {
        talent: 'Talent',
        craft: 'Craft',
        results: 'Results',
        direction: 'Direction',
        culture: 'Culture',
      }

      return Object.keys(categoryMap).map((key) => ({
        category: categoryMap[key],
        managerScore: managerData
          ? parseFloat(
              String(
                managerData[`${key}Avg` as keyof typeof managerData] ?? '0',
              ),
            )
          : 0,
        directReportScore: directReportData
          ? parseFloat(
              String(
                directReportData[
                  `${key}Avg` as keyof typeof directReportData
                ] ?? '0',
              ),
            )
          : 0,
      }))
    }

    return []
  }, [averagesData])

  // If showOnlyScoreCards is true, only render the score cards
  if (showOnlyScoreCards) {
    if (isLoading) {
      return (
        <>
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-3 w-28 mb-3" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-3 w-32 mb-3" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
        </>
      )
    }

    return (
      <>
        <ScoreCard score={overallScores.managerAvg} label="Manager score" />
        <ScoreCard
          score={overallScores.directReportAvg}
          label="Direct report score"
        />
      </>
    )
  }

  // If showOnlySummary is true, only render the radar chart and category scores
  if (showOnlySummary) {
    if (isLoading) {
      return (
        <>
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-5 w-40 mb-6" />
              <div className="flex items-center justify-center py-12">
                <Skeleton className="w-64 h-64" />
              </div>
            </CardContent>
          </Card>
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-5 w-36 mb-6" />
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 rounded-md overflow-hidden">
                    <Skeleton className="h-full w-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )
    }

    return (
      <>
        <AssessmentRadarChart data={radarData} />
        <CategoryScores data={radarData} />
      </>
    )
  }

  // Default behavior: render score cards + full-width summary section
  return (
    <>
      {/* Score Cards */}
      {isLoading ? (
        <>
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-3 w-28 mb-3" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-3 w-32 mb-3" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <ScoreCard score={overallScores.managerAvg} label="Manager score" />
          <ScoreCard
            score={overallScores.directReportAvg}
            label="Direct report score"
          />
        </>
      )}

      {/* Category Scores and Radar Chart - Full Width Section */}
      {isLoading ? (
        <div className="col-span-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Feedback Radar Chart */}
            <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
              <CardContent className="p-0">
                <Skeleton className="h-5 w-40 mb-6" />
                <div className="flex items-center justify-center py-12">
                  <Skeleton className="w-64 h-64" />
                </div>
              </CardContent>
            </Card>

            {/* Summary Average */}
            <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
              <CardContent className="p-0">
                <Skeleton className="h-5 w-36 mb-6" />
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 rounded-md overflow-hidden">
                      <Skeleton className="h-full w-full" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="col-span-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            <AssessmentRadarChart data={radarData} />
            <CategoryScores data={radarData} />
          </div>
        </div>
      )}
    </>
  )
}
